import React, { useEffect, useRef } from "react";

import { Box, Divider, Drawer, Typography, useMediaQuery } from "@mui/material";

import ItemList from "./ItemList";
import { ajustarTamanhoFonteAdmin } from "../estilos/global";

const LARGURA_EXPANDIDA = 240;

const LARGURA_RECOLHIDA = 72;

const DIFERENCA_LARGURA = LARGURA_EXPANDIDA - LARGURA_RECOLHIDA;

export default function DrawerComponent({
  menuOpen,

  toggleDrawer,

  visualNovo = false,

  expandido = false,

  onCloseDesktop,

  acompanharMenu = false,
}) {
  const telaPequena = useMediaQuery("(max-width:899px)");

  const permanente = visualNovo && !telaPequena;

  const drawerPaperRef = useRef(null);

  // =========================================================
  // FECHAR AO NAVEGAR
  // =========================================================

  const fecharAoNavegar = () => {
    /*
     * Desktop:
     * escolheu uma opção → recolhe.
     */
    if (permanente && expandido) {
      onCloseDesktop?.();

      return;
    }

    /*
     * Mobile:
     * fecha Drawer temporário.
     */
    if (!permanente && menuOpen) {
      toggleDrawer();
    }
  };

  // =========================================================
  // CLIQUE FORA
  // =========================================================

  useEffect(() => {
    if (!permanente || !expandido) {
      return undefined;
    }

    const fecharAoClicarFora = (event) => {
      const painel = drawerPaperRef.current;

      if (!painel) {
        return;
      }

      /*
       * As três barras ficam fora do Drawer.
       *
       * Esse clique NÃO pode ser interpretado
       * como clique fora, porque é ele próprio
       * que abre/fecha o menu.
       */
      const botaoMenu = event.target?.closest?.(
        '[aria-label="Abrir ou recolher menu"]',
      );

      if (botaoMenu) {
        return;
      }

      /*
       * Clique dentro do menu.
       */
      if (painel.contains(event.target)) {
        return;
      }

      /*
       * Clique em qualquer área externa.
       */
      onCloseDesktop?.();
    };

    document.addEventListener("click", fecharAoClicarFora);

    return () => {
      document.removeEventListener("click", fecharAoClicarFora);
    };
  }, [permanente, expandido, onCloseDesktop]);

  // =========================================================
  // ESC
  // =========================================================

  useEffect(() => {
    if (!permanente || !expandido) {
      return undefined;
    }

    const fecharComEsc = (event) => {
      if (event.key === "Escape") {
        onCloseDesktop?.();
      }
    };

    document.addEventListener("keydown", fecharComEsc);

    return () => {
      document.removeEventListener("keydown", fecharComEsc);
    };
  }, [permanente, expandido, onCloseDesktop]);

  // =========================================================
  // DIMENSÕES
  // =========================================================

  /*
   * Nos quatro perfis, a reserva acompanha a área visível do menu.
   * Os demais layouts mantêm a reserva completa anterior.
   */
  const larguraReservada = permanente
    ? acompanharMenu && !expandido
      ? LARGURA_RECOLHIDA
      : LARGURA_EXPANDIDA
    : 0;

  /*
   * O painel físico continua com 240px.
   *
   * A animação da largura reservada não altera a largura física do painel.
   */
  const larguraPainel = visualNovo ? LARGURA_EXPANDIDA : 280;

  /*
   * Fechado:
   * revela apenas 72px.
   *
   * Aberto:
   * revela 240px.
   */
  const recortePainel =
    permanente && !expandido
      ? `inset(0 ${DIFERENCA_LARGURA}px 0 0)`
      : "inset(0 0 0 0)";

  // =========================================================
  // RENDERIZAÇÃO
  // =========================================================

  return (
    <Drawer
      anchor="left"
      variant={permanente ? "permanent" : "temporary"}
      open={permanente || menuOpen}
      onClose={toggleDrawer}
      PaperProps={{
        ref: drawerPaperRef,
      }}
      sx={{
        // =====================================================
        // ESPAÇO RESERVADO
        // =====================================================

        width: permanente ? larguraReservada : 0,

        transition: permanente && acompanharMenu
          ? expandido
            ? "width 210ms cubic-bezier(0.4, 0, 0.2, 1)"
            : "width 180ms cubic-bezier(0.4, 0, 0.2, 1) 40ms"
          : undefined,

        bgcolor: permanente ? "#ffffff" : undefined,

        flexShrink: 0,

        position: permanente ? "relative" : undefined,

        overflow: permanente ? "visible" : undefined,

        zIndex: permanente ? 20 : undefined,

        // =====================================================
        // PAINEL
        // =====================================================

        "& .MuiDrawer-paper": {
          width: larguraPainel,

          boxSizing: "border-box",

          overflowX: "hidden",

          bgcolor: visualNovo ? "#ffffff" : "#f4f6f8",

          ...(permanente
            ? {
                position: "absolute",

                top: 0,

                left: 0,

                bottom: 0,

                height: "100%",

                /*
                 * A largura nunca muda.
                 * Somente a área visível.
                 */
                clipPath: recortePainel,

                transition: expandido
                  ? [
                      "clip-path 210ms cubic-bezier(0.4, 0, 0.2, 1)",
                      "box-shadow 150ms ease",
                    ].join(", ")
                  : [
                      "clip-path 180ms cubic-bezier(0.4, 0, 0.2, 1) 40ms",
                      "box-shadow 110ms ease 30ms",
                    ].join(", "),

                willChange: "clip-path",

                borderRight: "none",

                boxShadow: expandido
                  ? "6px 0 18px rgba(32, 79, 122, 0.10)"
                  : "none",

                zIndex: 20,
              }
            : {
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.14)",
              }),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          flexDirection: "column",

          height: "100%",

          minHeight: permanente ? 420 : "100vh",

          pt: visualNovo ? 1.5 : 2,

          pb: 2,

          overflow: "hidden",
        }}
      >
        {/* ===================================================
            MENU ANTIGO
        =================================================== */}

        {!visualNovo && (
          <>
            <Box
              sx={{
                px: 2,

                pb: 1.5,
              }}
            >
              <Typography
                sx={{
                  color: "#ffffff",

                  bgcolor: "#1976d2",

                  borderRadius: 2,

                  py: 1.5,

                  textAlign: "center",

                  fontSize: ajustarTamanhoFonteAdmin("1.1rem"),

                  fontWeight: 700,
                }}
              >
                Menu
              </Typography>
            </Box>

            <Divider />
          </>
        )}

        {/* ===================================================
            ITENS
        =================================================== */}

        <ItemList
          visualNovo={visualNovo}
          expandido={permanente ? expandido : true}
          onNavigate={fecharAoNavegar}
        />

        {/* ===================================================
            RODAPÉ ANTIGO
        =================================================== */}

        {!visualNovo && (
          <Box
            sx={{
              mx: 2,

              mt: "auto",

              p: 2,

              bgcolor: "#1565c0",

              borderRadius: 2,

              color: "#ffffff",

              textAlign: "center",
            }}
          >
            <Typography variant="body2">
              SRE - Superintendência Regional de Ensino
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
